import React, { FC, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { BlueButton, Box, Input, SearchInputWrapper } from '~Styles';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { useButton, useDebounce, useI18n, useSelector } from '~Hooks';
import { handyBreakpoint } from '~Styles/breakpoints';
import useBongQuery from '~Scenes/POS/Queries/query';
import LoadingDataScreen from '~ComponentsImproved/LoadingDataScreen';
import { useHistory } from 'react-router';
import Categories from './Components/Categories';
import Products from './Components/Products';
import Tabs, { Tab } from '~Scenes/Competitions/Components/Tabs';
import Icon from '~ComponentsImproved/Icon';
import CategoryIcon from '~Assets/icons/category.svg';
import ProductIcon from '~Assets/icons/product.svg';
import VariationsIcon from '~Assets/icons/variations.svg';
import Variations from './Components/Variations';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import BatchUpdateProducts from './Components/BatchUpdateProducts';

const Wrapper = styled(Box)`
  flex: 1;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: calc(100vh - 128px);
  position: fixed;
  width: 100%;
  height: 100%;
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  margin-top: 4px;
  padding: 20px 40px 160px 20px;
  box-sizing: border-box;

  ${handyBreakpoint} {
    padding: 16px;
  }
`;

const StyledTab = styled(Tab)`
  flex-flow: row nowrap;
`;

const BatchUpdateButton = styled(BlueButton)`
  white-space: nowrap;
  width: 304px;
  height: 44.5px;
`;

type TabType = 'categories' | 'products' | 'variations';

const Inventory: FC = () => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId);
  const history = useHistory();
  const bongQuery = useBongQuery(venueId);

  const batchUpdateModal = useModal();
  const batchUpdateButton = useButton({
    onClick: batchUpdateModal.toggle,
  });

  const [activeTab, setActiveTab] = useState<TabType>('categories');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  if (bongQuery.loading) {
    return (
      <LoadingDataScreen
        yOffset={24}
        animateOnMount={true}
        progress={bongQuery.progress}
        progressText={bongQuery.progressText}
        onClose={() => {
          if (window.history.length > 1) {
            window.history.back();
          } else {
            history.push('gateway');
          }
        }}
      />
    );
  }

  return (
    <>
      <Wrapper>
        <BackTopBar title={i18n('Gateway.Inventory')} dark />

        <ScrollWrapper>
          <Box display="flex" alignItems="center" gap="medium" marginBottom="small">
            <SearchInputWrapper fluid value={searchTerm} onClear={() => setSearchTerm('')}>
              <Input
                id="search-user"
                name="search-user"
                placeholder={
                  activeTab === 'categories'
                    ? i18n('Inventory.SearchCategories')
                    : activeTab === 'products'
                    ? i18n('Inventory.SearchProducts')
                    : i18n('Inventory.SearchVariations')
                }
                value={searchTerm}
                onChange={handleSearchChange}
                dark
                padding="small"
                isSearch
                autoFocus
                fluid
              />
            </SearchInputWrapper>
            <BatchUpdateButton {...batchUpdateButton}>
              {i18n('Inventory.UpdateProductsStock')}
            </BatchUpdateButton>
          </Box>

          <Tabs>
            <StyledTab
              display="flex"
              alignItems="center"
              gap="xs"
              active={activeTab === 'categories'}
              onClick={() => setActiveTab('categories')}>
              <Icon src={CategoryIcon} />
              {i18n('Competitions.SpecificSales.Categories')}
            </StyledTab>
            <StyledTab
              display="flex"
              alignItems="center"
              gap="xs"
              active={activeTab === 'products'}
              onClick={() => setActiveTab('products')}>
              <Icon src={ProductIcon} />
              {i18n('Competitions.SpecificSales.Products')}
            </StyledTab>
            <StyledTab
              display="flex"
              alignItems="center"
              gap="xs"
              active={activeTab === 'variations'}
              onClick={() => setActiveTab('variations')}>
              <Icon src={VariationsIcon} />
              {i18n('Inventory.Variations')}
            </StyledTab>
          </Tabs>

          {activeTab === 'categories' ? (
            <Categories
              categories={bongQuery.data?.company.categories || []}
              search={debouncedSearchTerm}
            />
          ) : activeTab === 'products' ? (
            <Products items={bongQuery.data?.company.items || []} search={debouncedSearchTerm} />
          ) : (
            <Variations
              items={bongQuery.data?.company.items || []}
              modificationGroups={bongQuery.data?.company?.modificationGroups || []}
              search={debouncedSearchTerm}
            />
          )}
        </ScrollWrapper>
      </Wrapper>

      <Modal isShowing={batchUpdateModal.isShowing} backdropClose={false}>
        <BatchUpdateProducts
          items={bongQuery.data?.company.items || []}
          onClose={batchUpdateModal.toggle}
        />
      </Modal>
    </>
  );
};

export default Inventory;
